import React, {useCallback, useEffect, useState} from 'react';

import {DateRangeInput, FormInput, NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {useProducts} from 'src/hooks/dictionaries/useProducts';
import {createAccountProductAccessTerm} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {useGateValue} from '@pb/kit-lib-statsig';

type Props = {
    accountId: number,
    onBusyChange: (busy: boolean) => void,
    onClose: () => void,
    onCreated: () => void,
};

const SUCCESS_CLOSE_DELAY = 5000;

export const NewAccessTermModal: React.FC<Props> = ({accountId, onBusyChange, onClose, onCreated}) => {
    const provisioningHistoryEnabled = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const apiDispatch = useAppDispatch();
    const {products, status: productsStatus, error: productsError, loadProducts} = useProducts();
    const [productExternalKey, setProductExternalKey] = useState('');
    const [referenceId, setReferenceId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [seats, setSeats] = useState<number | undefined>();
    const [serverError, setServerError] = useState('');
    const [busy, setBusy] = useState(false);

    const hasInvalidRange = Boolean(startDate && endDate && startDate > endDate);
    const hasInvalidSeats = seats !== undefined && seats < 1;
    const canCreate = Boolean(productExternalKey && startDate && endDate && !hasInvalidRange && !hasInvalidSeats);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const resetForm = useCallback(() => {
        setProductExternalKey('');
        setReferenceId('');
        setStartDate('');
        setEndDate('');
        setSeats(undefined);
        setServerError('');
    }, []);

    const onCancel = useCallback(() => {
        if (!busy) {
            resetForm();
            onClose();
        }
    }, [busy, onClose, resetForm]);

    const onCreate = useCallback(async () => {
        if (!canCreate || busy) {
            return;
        }

        setBusy(true);
        onBusyChange(true);
        setServerError('');

        const response = await apiDispatch(createAccountProductAccessTerm({
            accountId,
            createProductAccessTermRequest: {
                productExternalKey,
                ...(referenceId ? {referenceId} : {}),
                startDate,
                endDate,
                ...(seats !== undefined ? {seats} : {}),
            },
        }));

        if (!response.payload?.success) {
            setServerError(getError({error: response.payload?.data, separator: '. '}));
            setBusy(false);
            onBusyChange(false);
            return;
        }

        await new Promise(resolve => setTimeout(resolve, SUCCESS_CLOSE_DELAY));
        resetForm();
        onCreated();
    }, [
        accountId, busy, canCreate, endDate, onBusyChange, onCreated, productExternalKey,
        referenceId, resetForm, seats, startDate, apiDispatch,
    ]);

    return (
        <>
            <DialogHeader onClose={onCancel}>
                Create Product Access Term
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="l">
                    {serverError ? <Message color="error">{serverError}</Message> : null}
                    {productsError ? (
                        <Message color="error">
                            {getError({error: productsError, separator: '. '})}
                            {provisioningHistoryEnabled ? (
                                <Button variant="Type.TERTIARY" onClick={loadProducts}>Retry</Button>
                            ) : null}
                        </Message>
                    ) : null}
                    <Item>
                        <Label><RequiredMark label="Product"/></Label>
                        <ComboBox
                            value={productExternalKey}
                            disabled={productsStatus === 'idle' || productsStatus === 'loading' || busy}
                            placeholder="Select product"
                            onChange={setProductExternalKey}>
                            {products.map(product => (
                                <ComboboxItem
                                    key={product.productExternalKey}
                                    value={product.productExternalKey}
                                    caption={product.name}
                                />
                            ))}
                        </ComboBox>
                    </Item>
                    <FormInput
                        label="Contract ID"
                        placeholder="Enter contract ID"
                        maxLength={255}
                        value={referenceId}
                        disabled={busy}
                        onChange={setReferenceId}
                    />
                    <Group gap="l" alignItems="start">
                        <DateRangeInput
                            width={210}
                            preventPastDate={true}
                            id="product-access-term-start-date"
                            label={<RequiredMark label="Start Date"/>}
                            selectRangeType="start"
                            startDate={startDate}
                            endDate={endDate}
                            disabled={busy}
                            clearable={true}
                            onChange={setStartDate}
                        />
                        <DateRangeInput
                            width={210}
                            id="product-access-term-end-date"
                            preventPastDate={true}
                            label={<RequiredMark label="End Date"/>}
                            selectRangeType="end"
                            startDate={startDate}
                            endDate={endDate}
                            disabled={busy}
                            clearable={true}
                            onChange={setEndDate}
                        />
                    </Group>
                    {hasInvalidRange ? <Message color="error">End date cannot be before the start date</Message> : null}
                    <NumberInput
                        label="Seats"
                        value={seats}
                        displayedValue={seats?.toString() || ''}
                        placeholder="Unlimited"
                        maxValue={999999}
                        inputType={NumberInputTypes.POSITIVE_INTEGER}
                        minValue={1}
                        disabled={busy}
                        onChange={setSeats}
                        onBlur={setSeats}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} disabled={!canCreate || busy} onClick={onCreate}>
                        Create Term
                    </Button>
                    <Button variant="Type.SECONDARY" busy={busy} disabled={busy} onClick={onCancel}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
